// src/services/attendanceUtils.js
import moment from 'moment';
import dayjs from 'dayjs';

// NEW: Format hours as HH:MM instead of decimal
export const formatHoursMinutes = (decimalHours) => {
  if (!decimalHours && decimalHours !== 0) return '0h 00m';
  
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  
  return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
};

// NEW: Parse leave type string to get hours
export const parseLeaveType = (leaveType) => {
  if (!leaveType) return { type: null, hours: 0 };
  
  const mapping = {
    'SICK 5': { type: 'SICK LEAVE', hours: 5 },
    'SICK 8': { type: 'SICK LEAVE', hours: 8 },
    'SICK 9': { type: 'SICK LEAVE', hours: 9 },
    'LOCAL LEAVE 5': { type: 'LOCAL LEAVE', hours: 5 },
    'LOCAL LEAVE 8': { type: 'LOCAL LEAVE', hours: 8 },
    'LOCAL LEAVE 9': { type: 'LOCAL LEAVE', hours: 9 },
    'PH 5': { type: 'PH', hours: 5 },
    'PH 8': { type: 'PH', hours: 8 },
    'PH 9': { type: 'PH', hours: 9 },
    'ABSENT': { type: 'ABSENT', hours: 0 },
    'OFF': { type: 'OFF', hours: 0 }
  };
  
  return mapping[leaveType] || { type: null, hours: 0 };
};

// NEW: Get leave type options for dropdown
export const getLeaveTypeOptions = () => {
  return [
    { label: 'Sick Leave - 5 hours', value: 'SICK 5' },
    { label: 'Sick Leave - 8 hours', value: 'SICK 8' },
    { label: 'Sick Leave - 9 hours', value: 'SICK 9' },
    { label: 'Local Leave - 5 hours', value: 'LOCAL LEAVE 5' },
    { label: 'Local Leave - 8 hours', value: 'LOCAL LEAVE 8' },
    { label: 'Local Leave - 9 hours', value: 'LOCAL LEAVE 9' },
    { label: 'Public Holiday - 5 hours', value: 'PH 5' },
    { label: 'Public Holiday - 8 hours', value: 'PH 8' },
    { label: 'Public Holiday - 9 hours', value: 'PH 9' },
    { label: 'Absent', value: 'ABSENT' },
    { label: 'Day Off', value: 'OFF' }
  ];
};

export const calculateAttendanceStats = (attendance) => {
  const stats = {
    totalDays: attendance.length,
    lateDays: 0,
    earlyOutDays: 0,
    regularDays: 0,
    totalHours: 0,
    presentDays: 0,
    absentDays: 0,
    employeeStats: {}
  };

  attendance.forEach(record => {
    // Update general stats
    if (record.is_late) stats.lateDays++;
    if (record.is_early_out) stats.earlyOutDays++;
    if (!record.is_late && !record.is_early_out) stats.regularDays++;
    if (record.status === 'present') stats.presentDays++;
    if (record.status === 'absent') stats.absentDays++;
    stats.totalHours += parseFloat(record.total_hours) || 0;

    // Update employee stats
    const empId = record.employees?.id || record.employee_id;
    if (empId) {
      if (!stats.employeeStats[empId]) {
        stats.employeeStats[empId] = {
          id: empId,
          name: `${record.employees?.first_name || ''} ${record.employees?.last_name || ''}`.trim(),
          department: record.employees?.department,
          totalDays: 0,
          lateDays: 0,
          earlyOutDays: 0,
          regularDays: 0,
          presentDays: 0,
          absentDays: 0,
          totalHours: 0,
          averageHours: 0
        };
      }

      const empStats = stats.employeeStats[empId];
      empStats.totalDays++;
      if (record.is_late) empStats.lateDays++;
      if (record.is_early_out) empStats.earlyOutDays++;
      if (!record.is_late && !record.is_early_out) empStats.regularDays++;
      if (record.status === 'present') empStats.presentDays++;
      if (record.status === 'absent') empStats.absentDays++;
      empStats.totalHours += parseFloat(record.total_hours) || 0;
      empStats.averageHours = empStats.totalHours / empStats.totalDays;
    }
  });

  return stats;
};

export const generateAttendanceReport = (attendance, dateRange) => {
  const stats = calculateAttendanceStats(attendance);
  
  return {
    period: {
      start: dateRange[0].format('DD/MM/YYYY'),
      end: dateRange[1].format('DD/MM/YYYY'),
      totalDays: dateRange[1].diff(dateRange[0], 'days') + 1
    },
    summary: {
      totalEmployees: Object.keys(stats.employeeStats).length,
      totalDays: stats.totalDays,
      presentDays: stats.presentDays,
      absentDays: stats.absentDays,
      averageHours: stats.totalHours / stats.totalDays || 0,
      regularAttendance: (stats.regularDays / stats.totalDays * 100) || 0,
      latePercentage: (stats.lateDays / stats.totalDays * 100) || 0,
      earlyOutPercentage: (stats.earlyOutDays / stats.totalDays * 100) || 0
    },
    departmentStats: Object.values(stats.employeeStats).reduce((acc, emp) => {
      const dept = emp.department || 'Unassigned';
      if (!acc[dept]) {
        acc[dept] = {
          totalEmployees: 0,
          totalDays: 0,
          totalHours: 0,
          regularDays: 0,
          lateDays: 0,
          presentDays: 0,
          absentDays: 0,
          averageHours: 0
        };
      }
      acc[dept].totalEmployees++;
      acc[dept].totalDays += emp.totalDays;
      acc[dept].totalHours += emp.totalHours;
      acc[dept].regularDays += emp.regularDays;
      acc[dept].lateDays += emp.lateDays;
      acc[dept].presentDays += emp.presentDays;
      acc[dept].absentDays += emp.absentDays;
      acc[dept].averageHours = acc[dept].totalHours / acc[dept].totalEmployees;
      return acc;
    }, {}),
    employeeStats: stats.employeeStats
  };
};

export const formatTime = (timeStr) => {
  if (!timeStr) return '--:--';
  
  try {
    if (timeStr.match(/^\d{2}:\d{2}$/)) {
      return timeStr;
    }
    
    if (timeStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
      return timeStr.substring(0, 5);
    }
    
    const parsed = moment(timeStr, ['HH:mm:ss', 'HH:mm', 'h:mm A', 'h:mm:ss A']);
    if (parsed.isValid()) {
      return parsed.format('HH:mm');
    }
    
    return '--:--';
  } catch (error) {
    console.warn('Error formatting time:', timeStr, error);
    return '--:--';
  }
};

export const getAttendanceStatus = (record) => {
  if (!record) return { text: 'Unknown', color: 'default' };
  
  // Check for leave types
  if (record.leave_type) {
    switch (record.leave_type) {
      case 'SICK LEAVE':
        return { text: 'Sick Leave', color: 'orange' };
      case 'LOCAL LEAVE':
        return { text: 'Local Leave', color: 'blue' };
      case 'PH':
        return { text: 'Public Holiday', color: 'purple' };
      case 'ABSENT':
        return { text: 'Absent', color: 'red' };
      case 'OFF':
        return { text: 'Day Off', color: 'default' };
    }
  }
  
  if (record.status === 'absent') {
    return { text: 'Absent', color: 'red' };
  }
  
  if (record.is_late && record.is_early_out) {
    return { text: 'Irregular', color: 'red' };
  }
  
  if (record.is_late) {
    return { text: 'Late', color: 'orange' };
  }
  
  if (record.is_early_out) {
    return { text: 'Early Out', color: 'orange' };
  }
  
  return { text: 'Regular', color: 'green' };
};

export const formatDuration = (hours) => {
  if (!hours && hours !== 0) return '--:--';
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

export const calculateOvertime = (totalHours, standardHours = 8) => {
  if (!totalHours) return 0;
  const overtime = Math.max(0, totalHours - standardHours);
  return Number(overtime.toFixed(2));
};

export const validateAttendanceData = (data) => {
  const errors = [];
  const warnings = [];
  
  if (!data || data.length === 0) {
    errors.push('No attendance data provided');
    return { isValid: false, errors, warnings };
  }
  
  const requiredFields = ['employee_id', 'date'];
  const firstRecord = data[0];
  
  requiredFields.forEach(field => {
    if (!(field in firstRecord)) {
      errors.push(`Missing required field: ${field}`);
    }
  });
  
  let duplicateCount = 0;
  let invalidTimeCount = 0;
  const seenRecords = new Set();
  
  data.forEach((record, index) => {
    const recordKey = `${record.employee_id}_${record.date}`;
    if (seenRecords.has(recordKey)) {
      duplicateCount++;
    } else {
      seenRecords.add(recordKey);
    }
    
    if (record.check_in && !isValidTimeFormat(record.check_in)) {
      invalidTimeCount++;
    }
    if (record.check_out && !isValidTimeFormat(record.check_out)) {
      invalidTimeCount++;
    }
    
    if (record.total_hours && parseFloat(record.total_hours) < 0) {
      warnings.push(`Negative hours found for record at index ${index}`);
    }
  });
  
  if (duplicateCount > 0) {
    warnings.push(`Found ${duplicateCount} potential duplicate records`);
  }
  
  if (invalidTimeCount > 0) {
    warnings.push(`Found ${invalidTimeCount} records with invalid time format`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    stats: {
      totalRecords: data.length,
      duplicates: duplicateCount,
      invalidTimes: invalidTimeCount
    }
  };
};

export const isValidTimeFormat = (timeStr) => {
  if (!timeStr) return false;
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
  return timeRegex.test(timeStr);
};

export const calculateWorkingDays = (startDate, endDate, excludeWeekends = true, holidays = []) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  let workingDays = 0;
  let current = start;
  
  while (current.isSameOrBefore(end)) {
    const isWeekend = current.day() === 0 || current.day() === 6;
    const isHoliday = holidays.some(holiday => current.format('YYYY-MM-DD') === holiday);
    
    if (!isHoliday && (!excludeWeekends || !isWeekend)) {
      workingDays++;
    }
    
    current = current.add(1, 'day');
  }
  
  return workingDays;
};

export const getAttendancePercentage = (presentDays, totalDays) => {
  if (totalDays === 0) return 0;
  return Math.round((presentDays / totalDays) * 100);
};

export const formatAttendancePercentage = (percentage) => {
  if (percentage >= 95) return { value: percentage, status: 'excellent', color: 'green' };
  if (percentage >= 85) return { value: percentage, status: 'good', color: 'blue' };
  if (percentage >= 75) return { value: percentage, status: 'average', color: 'orange' };
  return { value: percentage, status: 'poor', color: 'red' };
};

export const parseTimeString = (timeStr) => {
  if (!timeStr) return null;
  
  try {
    const ampmMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (ampmMatch) {
      let hours = parseInt(ampmMatch[1]);
      const minutes = parseInt(ampmMatch[2]);
      const period = ampmMatch[3].toUpperCase();
      
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      
      return { hours, minutes };
    }
    
    const hourMatch = timeStr.match(/(\d{1,2}):(\d{2})/);
    if (hourMatch) {
      return {
        hours: parseInt(hourMatch[1]),
        minutes: parseInt(hourMatch[2])
      };
    }
    
    return null;
  } catch (error) {
    console.warn('Error parsing time string:', timeStr, error);
    return null;
  }
};

export const calculateTimeDifference = (startTime, endTime) => {
  const start = parseTimeString(startTime);
  const end = parseTimeString(endTime);
  
  if (!start || !end) return 0;
  
  let startMinutes = start.hours * 60 + start.minutes;
  let endMinutes = end.hours * 60 + end.minutes;
  
  if (endMinutes < startMinutes) {
    endMinutes += 24 * 60;
  }
  
  const diffMinutes = endMinutes - startMinutes;
  return Math.round((diffMinutes / 60) * 100) / 100;
};

export const isLateArrival = (checkInTime, standardStartTime = '09:00') => {
  const checkIn = parseTimeString(checkInTime);
  const standard = parseTimeString(standardStartTime);
  
  if (!checkIn || !standard) return false;
  
  const checkInMinutes = checkIn.hours * 60 + checkIn.minutes;
  const standardMinutes = standard.hours * 60 + standard.minutes;
  
  return checkInMinutes > standardMinutes;
};

export const isEarlyDeparture = (checkOutTime, standardEndTime = '17:00') => {
  const checkOut = parseTimeString(checkOutTime);
  const standard = parseTimeString(standardEndTime);
  
  if (!checkOut || !standard) return false;
  
  const checkOutMinutes = checkOut.hours * 60 + checkOut.minutes;
  const standardMinutes = standard.hours * 60 + standard.minutes;
  
  return checkOutMinutes < standardMinutes;
};

export const generateDetailedEmployeeReport = (employeeId, attendance, dateRange) => {
  const employeeRecords = attendance.filter(record => 
    record.employee_id === employeeId || record.employees?.id === employeeId
  );
  
  if (employeeRecords.length === 0) {
    return null;
  }
  
  const employee = employeeRecords[0].employees;
  const stats = calculateAttendanceStats(employeeRecords);
  const totalWorkingDays = calculateWorkingDays(dateRange[0], dateRange[1]);
  
  return {
    employee: {
      id: employeeId,
      name: `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim(),
      department: employee?.department || 'Unassigned',
      fingerprint_id: employee?.fingerprint_id || employee?.employee_number
    },
    period: {
      start: dateRange[0].format('DD/MM/YYYY'),
      end: dateRange[1].format('DD/MM/YYYY'),
      totalWorkingDays
    },
    attendance: {
      totalRecords: employeeRecords.length,
      presentDays: stats.presentDays,
      absentDays: totalWorkingDays - stats.presentDays,
      lateDays: stats.lateDays,
      earlyOutDays: stats.earlyOutDays,
      regularDays: stats.regularDays,
      attendanceRate: getAttendancePercentage(stats.presentDays, totalWorkingDays)
    },
    hours: {
      totalHours: stats.totalHours,
      averageHours: stats.totalHours / stats.presentDays || 0,
      overtime: employeeRecords.reduce((sum, record) => 
        sum + calculateOvertime(parseFloat(record.total_hours)), 0
      )
    },
    records: employeeRecords.map(record => ({
      date: record.date,
      checkIn: record.check_in,
      checkOut: record.check_out,
      totalHours: record.total_hours,
      status: getAttendanceStatus(record),
      isLate: record.is_late,
      isEarlyOut: record.is_early_out,
      isWeekend: record.is_sunday,
      isHoliday: record.is_holiday
    }))
  };
};

export default {
  calculateAttendanceStats,
  generateAttendanceReport,
  formatTime,
  getAttendanceStatus,
  formatDuration,
  formatHoursMinutes,
  calculateOvertime,
  validateAttendanceData,
  isValidTimeFormat,
  calculateWorkingDays,
  getAttendancePercentage,
  formatAttendancePercentage,
  parseTimeString,
  calculateTimeDifference,
  isLateArrival,
  isEarlyDeparture,
  generateDetailedEmployeeReport,
  parseLeaveType,
  getLeaveTypeOptions
};